import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Info, MapPin, UtensilsCrossed } from "lucide-react";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";
import { Reveal } from "@/components/motion";

// Danish Christmas food (julefrokost) guide — 16 restaurants the site owner
// confirmed serve traditional Christmas food for 2026. Grouped by area;
// every name links to Google Maps. Add/remove places by editing GROUPS.
const mapsUrl = (name, address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${address}, Copenhagen`)}`;

const GROUPS = [
  {
    area: "Indre By — city centre",
    places: [
      { name: "Aamanns 1921", address: "Niels Hemmingsens Gade 19-21" },
      { name: "Café Gammeltorv", address: "Gammeltorv 20" },
      { name: "Café Sorgenfri", address: "Brolæggerstræde 8" },
      { name: "Husmanns Vinstue", address: "Larsbjørnsstræde 2" },
      { name: "Restaurant Karla", address: "Dantes Plads 1" },
      { name: "Restaurant Kronborg", address: "Brolæggerstræde 12" },
      { name: "Restaurant Maven", address: "Nikolaj Plads 10" },
      { name: "Restaurant Schønnemann", address: "Hauser Plads 16" },
      { name: "Tivolihallen", address: "Vester Voldgade 91" },
    ],
  },
  {
    area: "Nyhavn & the royal quarter",
    places: [
      { name: "Nyhavns Færgekro", address: "Nyhavn 5" },
      { name: "Restaurant Sankt Annæ", address: "Sankt Annæ Plads 12" },
      { name: "Restaurant Vita", address: "Store Kongensgade 25" },
      { name: "Toldbod Bodega", address: "Esplanaden 4" },
    ],
  },
  {
    area: "Christianshavn",
    places: [
      { name: "Aamanns Genbo", address: "Kildepladsen 8" },
      { name: "Christianshavns Færgecafé", address: "Strandgade 50" },
    ],
  },
  {
    area: "Frederiksberg",
    places: [{ name: "Hansens Gamle Familiehave", address: "Pile Allé 10-12" }],
  },
];

// Page FAQ — answers stay plain strings so the FAQPage schema below reuses them.
const FAQS = [
  {
    q: "What is a Danish julefrokost?",
    a: "The Danish Christmas lunch — a long, multi-course affair that runs through November and December: pickled herring on rye to start, then liver pâté, flæskesteg (roast pork with crackling) and more, washed down with beer and snaps, ending with risalamande. Restaurants serve it as a set menu — ask for the 'juleplatte' for a bit of everything.",
  },
  {
    q: "Where can tourists eat traditional Danish Christmas food?",
    a: "At the sixteen restaurants on this page — all confirmed to serve traditional Christmas food this season, grouped by neighbourhood so you can pick one near your plans. Schønnemann and Aamanns 1921 are the famous ones; the bodegas like Café Sorgenfri are the old-school charmers.",
  },
  {
    q: "Do I need to book Christmas restaurants in Copenhagen in advance?",
    a: "Yes — julefrokost season is the busiest restaurant season of the year in Denmark, and the traditional places fill up with company parties and family lunches. Book weeks ahead for December weekends; weekday lunches are easier.",
  },
  {
    q: "Are restaurants open on 24 December in Copenhagen?",
    a: "Mostly no — Danes celebrate Christmas at home on the evening of the 24th, and nearly all restaurants close. The julefrokost season runs through November and up to around the 23rd. If you're in town over the 24th–26th, check hotel restaurants, which often serve Christmas dinners for guests.",
  },
  {
    q: "What is risalamande?",
    a: "The Danish Christmas dessert — cold rice pudding folded with whipped cream and chopped almonds, served with warm cherry sauce. One portion hides a whole almond, and whoever finds it wins the 'almond present' (mandelgave). Every julefrokost ends with it.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function ChristmasFoodPage() {
  return (
    <main data-testid="christmas-food-page" className="bg-[#F9F8F6] text-black">
      <Seo
        title="Danish Christmas Food in Copenhagen: 16 Restaurants"
        description="Where to eat a real Danish julefrokost in Copenhagen — 16 restaurants serving traditional Christmas food in 2026, with addresses and booking tips."
        path="/christmas-food"
      />
      <PageSchema
        path="/christmas-food"
        name="Danish Christmas Food in Copenhagen"
        description="Where to eat a real Danish julefrokost in Copenhagen — 16 restaurants serving traditional Christmas food."
        breadcrumb="Christmas food"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <header className="sticky top-0 z-40 bg-black border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-3 flex items-center justify-between">
          <Link
            to="/christmas"
            className="inline-flex items-center gap-2 font-body font-700 text-sm text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Christmas in Copenhagen
          </Link>
          <ShareButton />
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden bg-[#13141A] text-[#F5EFE0]">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1701944578057-0be983834747?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxDb3BlbmhhZ2VuJTIwQ2hyaXN0bWFzJTIwZm9vZCUyMG1hcmtldCUyMHN0YWxsfGVufDB8fHx8MTc5MTQ2MTA0OHww&ixlib=rb-4.1.0&q=85" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28 grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
          <Reveal y={30}>
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4">
              JULEFROKOST · THE DANISH CHRISTMAS TABLE
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
              Where to eat a proper{" "}
              <span className="font-editorial italic font-600">Danish Christmas food in Copenhagen</span>
            </h1>
            <p className="font-body text-base md:text-lg text-[#F5EFE0]/70 mt-6 max-w-2xl">
              The julefrokost is Denmark's long, glorious Christmas lunch: pickled herring, fried
              plaice, frikadeller, flæskesteg with crackling, risalamande — and snaps, plenty of
              snaps. These 16 restaurants have confirmed they're serving traditional Christmas
              food this season. Book ahead — December tables go fast.
            </p>
            <p data-testid="christmas-food-updated" className="font-body text-xs uppercase tracking-[0.2em] text-[#F5EFE0]/40 mt-4">
              Last updated: 29 September 2026
            </p>
          </Reveal>
          <Reveal y={30} delay={0.1}>
            <img
              src="/images/food/flaeskesteg.webp"
              alt="Flæskesteg — Danish roast pork with crispy crackling, the centrepiece of a Christmas lunch"
              data-testid="christmas-food-hero-img"
              className="w-full aspect-[4/3] object-cover rounded-2xl border border-white/20 shadow-brut rotate-1"
            />
          </Reveal>
        </div>
      </section>

      {/* the December table — photo strip */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-14 md:pt-20">
        <Reveal y={20}>
          <div className="grid grid-cols-3 gap-3 md:gap-6">
            {[
              { img: "/images/food/risengrod.webp", name: "Risengrød", alt: "Risengrød — hot rice porridge with butter, cinnamon and sugar" },
              { img: "/images/food/aebleskiver.webp", name: "Æbleskiver", alt: "Æbleskiver — round pancake puffs with powdered sugar and jam" },
              { img: "/images/food/risalamande.webp", name: "Risalamande", alt: "Risalamande — rice pudding with warm cherry sauce" },
            ].map((d) => (
              <figure key={d.name}>
                <img
                  src={d.img}
                  alt={d.alt}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover rounded-xl border border-zinc-200 shadow-brut"
                />
                <figcaption className="mt-2 font-body font-700 text-xs md:text-sm text-black/60 text-center">
                  {d.name}
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="font-body text-sm md:text-base text-black/50 text-center mt-6">
            The December table, three classics at a time. And no, æbleskiver are not the little
            Dutch puffs (poffertjes) — the Danish ones are bigger and properly round. More in{" "}
            <Link to="/danish-food" data-testid="christmas-food-foodguide-link" className="font-800 text-[#DF3226] underline underline-offset-4 hover:text-black transition-colors">
              the full Danish food guide
            </Link>
          </p>
        </Reveal>
      </section>

      {/* the restaurants, grouped by area */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
        {GROUPS.map((g) => (
          <div key={g.area} className="mt-12 first:mt-0">
            <Reveal y={20}>
              <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl">
                {g.area}
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {g.places.map((p, i) => (
                <Reveal key={p.name} delay={Math.min(i, 4) * 0.05} y={20}>
                  <div className="h-full bg-white border border-zinc-200 shadow-brut p-6 flex flex-col">
                    <h3 className="font-display font-900 tracking-tight text-2xl leading-none">
                      {p.name}
                    </h3>
                    <p className="mt-2 flex items-center gap-1.5 font-body text-sm text-black/50">
                      <MapPin size={13} className="text-[#DF3226] shrink-0" />
                      <a
                        href={mapsUrl(p.name, p.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`christmas-food-maps-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="hover:text-[#DF3226] hover:underline underline-offset-4 transition-colors"
                      >
                        {p.address}
                      </a>
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        ))}

        {/* the open slot — advertise */}
        <Reveal y={20}>
          <Link
            to="/advertise"
            data-testid="christmas-food-missing"
            className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border-2 border-dashed border-zinc-300 hover:border-[#DF3226] hover:-translate-y-0.5 transition-all p-6 md:p-8"
          >
            <div>
              <span className="inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                <UtensilsCrossed size={12} /> THIS SPOT IS OPEN
              </span>
              <h3 className="font-display font-900 tracking-tight text-2xl leading-none mt-2">
                Is your restaurant missing?
              </h3>
              <p className="font-body text-sm md:text-base text-black/70 mt-2">
                Serving a traditional julefrokost this December? Get in front of the travellers
                planning it right now.
              </p>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226]">
              Advertise with us
            </span>
          </Link>
        </Reveal>

        {/* practical notes */}
        <Reveal y={20}>
          <div
            data-testid="christmas-food-notes"
            className="mt-14 bg-white border border-zinc-200 shadow-brut p-6 md:p-8 flex flex-col md:flex-row gap-6"
          >
            <div className="flex items-start gap-3 flex-1">
              <Info size={18} className="text-[#DF3226] shrink-0 mt-0.5" />
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                <strong className="font-800 text-black">Book weeks ahead.</strong> Ask for the{" "}
                <em>julefrokost</em> menu specifically — most places serve it from mid-November
                until around 23 December.
              </p>
            </div>
            <div className="flex items-start gap-3 flex-1">
              <Info size={18} className="text-[#DF3226] shrink-0 mt-0.5" />
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                <strong className="font-800 text-black">25–26 December:</strong> many things are
                closed and transport runs at low capacity — plan your Christmas meal before the
                holidays themselves.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA — full-width band, same style as the other pages */}
      <section data-testid="christmas-food-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Markets, gløgg & lights?
            <br />
            <span className="font-editorial italic font-600">The full Christmas guide</span>
          </h2>
          <Link
            to="/christmas"
            data-testid="christmas-food-christmas-cta"
            className="shrink-0 inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Christmas in Copenhagen
          </Link>
        </div>
      </section>

      <GygTourBand tourId="1336936" label="GRAZE THROUGH THE CITY WITH A LOCAL" />
      <ExploreLinks
        testid="explore-christmas-food"
        links={[
          { label: "Markets, lights & J-Day — Christmas in Copenhagen", to: "/christmas" },
          { label: "The gløgg guide — six glasses worth seeking out", to: "/glogg" },
          { label: "The rest of the Danish table — the food guide", to: "/danish-food" },
          { label: "Open sandwiches, ranked — the smørrebrød guide", to: "/smorrebrod" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="christmas-food-faq" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Christmas food questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`christmas-food-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Danish Christmas Food in Copenhagen" />
    </main>
  );
}
